import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import BrandLogo from "./BrandLogo";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/vision", label: "Vision" },
  { to: "/leadership", label: "Leadership" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-[rgba(10,17,40,0.72)] backdrop-blur-xl border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-brand"
          className="flex items-center gap-3 group"
        >
          <BrandLogo variant="mark" className="h-10 md:h-11 w-auto drop-shadow-[0_0_12px_rgba(201,169,97,0.25)] transition-transform duration-500 group-hover:rotate-6" />
          <span className="font-display text-xl md:text-2xl tracking-[0.32em] text-ms-gold uppercase font-medium">
            Mindscope
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline font-body text-[0.78rem] tracking-[0.24em] uppercase pb-1 transition-colors duration-300 ${
                  isActive ? "text-ms-gold" : "text-white/80 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="nav-cta"
          className="hidden lg:inline-flex btn-ghost !py-2.5 !px-5 !text-[0.7rem]"
        >
          Begin Dialogue
        </Link>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ease-out ${
          open ? "max-h-[500px]" : "max-h-0"
        } bg-[#0A1128] border-b border-white/5`}
      >
        <div className="px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-body text-sm tracking-[0.22em] uppercase py-2 border-b border-white/5 ${
                  isActive ? "text-ms-gold" : "text-white/85"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/contact" data-testid="nav-mobile-cta" className="btn-gold mt-2 justify-center">
            Begin Dialogue
          </Link>
        </div>
      </div>
    </header>
  );
}
